/**
 * ProgressPanel Component
 *
 * Displays real-time progress for an iteration being processed by a Fargate worker.
 * Shows progress bar, task list, and status description with live updates via WebSocket.
 *
 * Adapted from husarz ProgressPanel implementation.
 *
 * Layout:
 * ┌────────────────────────────────────────────────────────────────────┐
 * │ ████████████████████░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░  45%  ETA: 3m │
 * ├────────────────────────────────────────────────────────────────────┤
 * │ ✓ Analyzing task requirements                                      │
 * │ ✓ Searching knowledge base                                         │
 * │ ○ Generating report                                                │
 * │ ○ Finalizing outputs                                               │
 * ├────────────────────────────────────────────────────────────────────┤
 * │ Currently searching for relevant documents in the knowledge base   │
 * │ to gather context for the analysis...                              │
 * └────────────────────────────────────────────────────────────────────┘
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIterationProgress } from '@/hooks/useIterationProgress';
import type { IterationProgress, ProgressTask } from '@/types/tasks';
import { formatElapsedTime } from '@/types/tasks';

interface ProgressPanelProps {
  /** Task ID for API calls */
  taskId: string;
  /** Iteration ID to track progress for */
  iterationId: string;
  /** Optional callback when progress completes (100%) */
  onComplete?: () => void;
  /** Optional custom class name */
  className?: string;
  /** Enable compact mode (smaller spacing) */
  compact?: boolean;
}

export default function ProgressPanel({
  taskId,
  iterationId,
  onComplete,
  className = '',
  compact = false,
}: ProgressPanelProps) {
  const { t } = useTranslation('tasks');
  const { progress, elapsedSeconds, isConnected, isLoading, error, reconnect } = useIterationProgress(
    iterationId,
    { taskId }
  );
  const [hasCompleted, setHasCompleted] = useState(false);

  // Call onComplete when progress reaches 100%
  useEffect(() => {
    if (progress?.progress_percent === 100 && !hasCompleted && onComplete) {
      setHasCompleted(true);
      onComplete();
    }
  }, [progress?.progress_percent, hasCompleted, onComplete]);

  // Reset completion state when iteration changes
  useEffect(() => {
    setHasCompleted(false);
  }, [iterationId]);

  // Show loading/connection status
  if (isLoading || (!isConnected && !progress)) {
    return (
      <div className={`rounded-lg border border-blue-200 bg-blue-50/50 ${className}`}>
        <div className={`${compact ? 'p-3' : 'p-4'}`}>
          {error ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-red-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm">{t('progress.connectionError', { error })}</span>
              </div>
              <button
                onClick={reconnect}
                className="px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 rounded-md transition-colors"
              >
                {t('progress.retry')}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-blue-600">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm">{isLoading ? t('progress.loading') : t('progress.connecting')}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // If we have progress data, show the panel
  return (
    <div className={`rounded-lg border border-blue-200 bg-blue-50/50 overflow-hidden ${className}`}>
      {/* Progress Bar Section */}
      <ProgressHeader progress={progress} elapsedSeconds={elapsedSeconds} compact={compact} />

      {/* Task List Section */}
      {progress?.progress_task_list && progress.progress_task_list.length > 0 && (
        <TaskList tasks={progress.progress_task_list} compact={compact} />
      )}

      {/* Status Description Section */}
      {progress?.progress_status_description && (
        <StatusDescription
          description={progress.progress_status_description}
          compact={compact}
        />
      )}

      {/* Connection indicator */}
      {!isConnected && (
        <div className="px-4 py-2 bg-yellow-50 border-t border-yellow-200">
          <div className="flex items-center justify-between">
            <span className="text-xs text-yellow-700">{t('progress.connectionLost')}</span>
            <button
              onClick={reconnect}
              className="text-xs text-yellow-700 hover:text-yellow-800 underline"
            >
              {t('progress.reconnect')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

interface ProgressHeaderProps {
  progress: IterationProgress | null;
  elapsedSeconds: number | null;  // Self-ticking elapsed from hook
  compact: boolean;
}

function ProgressHeader({ progress, elapsedSeconds, compact }: ProgressHeaderProps) {
  const percent = progress?.progress_percent ?? 0;
  const eta = progress?.progress_eta;
  const message = progress?.progress_message;

  return (
    <div className={`${compact ? 'p-3' : 'p-4'} border-b border-blue-100`}>
      {/* Message row */}
      {message && (
        <div className="flex items-center gap-2 mb-2">
          <svg className="w-4 h-4 text-blue-500 animate-pulse" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path fill="currentColor" d="M12 6v6l4 2" />
          </svg>
          <span className="text-sm font-medium text-blue-700">{message}</span>
        </div>
      )}

      {/* Progress bar row */}
      <div className="flex items-center gap-3">
        {/* Progress bar */}
        <div className="flex-1 h-2.5 bg-blue-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${percent}%` }}
          />
        </div>

        {/* Percentage */}
        <span className="text-sm font-semibold text-blue-700 tabular-nums min-w-[3rem] text-right">
          {percent}%
        </span>
      </div>

      {/* Time info row */}
      <div className="flex items-center justify-between mt-2 text-xs text-blue-600">
        {elapsedSeconds !== null && elapsedSeconds !== undefined && (
          <span>Elapsed: {formatElapsedTime(elapsedSeconds)}</span>
        )}
        {eta && <span className="font-medium">ETA: {eta}</span>}
      </div>
    </div>
  );
}

interface TaskListProps {
  tasks: ProgressTask[];
  compact: boolean;
}

function TaskList({ tasks, compact }: TaskListProps) {
  // Find the current (first incomplete) task
  const currentIndex = tasks.findIndex((t) => !t.completed);

  return (
    <div className={`${compact ? 'p-3' : 'p-4'} border-b border-blue-100`}>
      <div className="space-y-1.5">
        {tasks.map((task, index) => {
          const isComplete = task.completed;
          const isCurrent = index === currentIndex;

          return (
            <div
              key={index}
              className={`flex items-start gap-2 ${
                isComplete
                  ? 'text-green-600'
                  : isCurrent
                    ? 'text-blue-700 font-medium'
                    : 'text-gray-400'
              }`}
            >
              {/* Status icon */}
              <span className="mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center">
                {isComplete ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : isCurrent ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <span className="w-3 h-3 rounded-full border-2 border-current" />
                )}
              </span>

              {/* Task content */}
              <span className={`text-sm ${isComplete ? 'line-through opacity-75' : ''}`}>
                {task.content}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface StatusDescriptionProps {
  description: string;
  compact: boolean;
}

function StatusDescription({ description, compact }: StatusDescriptionProps) {
  return (
    <div className={`${compact ? 'p-3' : 'p-4'} bg-white/50`}>
      <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
}

// =============================================================================
// Exports
// =============================================================================

export { ProgressPanel };
